import { identityTransition$ } from "$root/functional-areas/session/atom";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import type { ReactNode } from "react";
import InfiniteLoader from "./InfiniteLoader";

/**
 * Renders nothing but a loader while the app is crossing an identity boundary.
 *
 * **Everything that persists per-user state belongs inside this.** A write to the per-user store is
 * attributed to whichever identity is current when its request is *sent*, so anything left mounted
 * across a crossing can re-render against the arriving identity's store and persist the outgoing
 * one's state under it — see `functional-areas/user/identity-boundary.ts` for the crossing itself.
 *
 * That is why it sits at the root rather than inside the router. Routed pages are not the only
 * persisters: a spawned dialog renders through `SpawnMountPoint`, which is a sibling of the router,
 * and the instrument adder keeps its own column layout. A gate inside the router would leave that
 * dialog mounted through the crossing — and `cleanupSession`'s `resetSpawned` then unmounts it after
 * the identity has already moved, which is the worst of both.
 */
export function IdentityTransitionGate({ children }: { children: ReactNode }): React.JSX.Element {
	const identityTransition = useAtomValue(identityTransition$);
	if (identityTransition) {
		return <InfiniteLoader style={{ minHeight: "100dvh" }} />;
	}
	return <>{children}</>;
}
